import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import search_button_compact from '../../assets/search_button_compact.png';
import search_button from '../../assets/search_button.png';
import search_icon from '../../assets/search_icon.png';
import search_button_small from '../../assets/search_button_small.png';
import search_button_small_active from '../../assets/search_button_small_active.png';
import city_radio_selected from '../../assets/city_radio_selected.png';
import city_radio_unselected from '../../assets/city_radio_unselected.png';
import '../../css/Directory.css';
import DirectoryCompany from "../components/DirectoryCompany";
import { CTAFooter } from "../components/common/CTAFooter";

function Directory() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cities, setCities] = useState([]);
  const [trades, setTrades] = useState([]);
  const [selectedCity, setSelectedCity] = useState('0');
  const [selectedTrades, setSelectedTrades] = useState([]);
  const [clickedSearchType, setClickedSearchType] = useState('');
  const searchSelectContainerRef = useRef(null);
  const [searchResult, setSearchResult] = useState({city: {}, trades: []});
  const [showSmallSearch, setShowSmallSearch] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/cities')
        .then(response => setCities(response.data))
        .catch(error => setError('Error fetching cities:'));
    axios.get('/api/trades')
        .then(response => setTrades(response.data))
        .catch(error => setError('Error fetching trades:'));
  }, []);

  useEffect(() => {
    const cityId = searchParams.get('c');
    const tradeIds = searchParams.get('t');

    if (cityId) setSelectedCity(Number(cityId));
    if (tradeIds) setSelectedTrades(tradeIds.split(',').map((id) => Number(id)));
  }, [searchParams]);

  useEffect(() => {
    if (selectedCity || selectedTrades.length > 0) {
      axios
          .get(`/api/search?city_id=${selectedCity}&trade_ids=${selectedTrades.join(',')}`)
          .then(response => setSearchResult(response.data))
          .catch(error => setError('Error fetching companies:'));
    }
  }, [selectedCity, selectedTrades]);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [clickedSearchType]);

  const handleSearchTypeClick = (type, event) => {
    if (event.target.classList.contains('directorySearchButton')) {
      return;
    }

    if (type === 'cities' || selectedCity) {
      setClickedSearchType(type);
    }
  }

  const handleOutsideClick = (event) => {
    if (searchSelectContainerRef.current && !searchSelectContainerRef.current.contains(event.target)) {
      setClickedSearchType('');
    }
  };

  const handleCitySelect = (cityId) => {
    setSelectedCity(cityId);
    setClickedSearchType('');
    updateSearchParams(cityId, selectedTrades);
  }

  const handleTradeSelectedChange = (tradeId) => {
    const updatedTrades = selectedTrades.includes(tradeId)
        ? selectedTrades.filter((id) => id !== tradeId)
        : [...selectedTrades, tradeId];

    setSelectedTrades(updatedTrades);
    updateSearchParams(selectedCity, updatedTrades);
  }

  const updateSearchParams = (cityId, tradeIds) => {
    const params = {};
    if (cityId) params.c = cityId;
    if (tradeIds.length > 0) params.t = tradeIds.join(',');

    setSearchParams(params);
  };

  const getCity = (cityId) => {
    return cities.find((city) => city.id === Number(cityId));
  }

  const getTrade = (tradeId) => {
    return trades.find((trade) => trade.id === Number(tradeId));
  }

  const getSelectedTradeNames = () => {
    const tradeNames = [];
    selectedTrades.forEach((tradeId) => {
      tradeNames.push(getTrade(tradeId)?.name);
    })
    return tradeNames.join(", ");
  }

  const handleSearch = async () => {
    if (selectedCity) {
      const response = await axios.get(`/api/search?city_id=${selectedCity}&trade_ids=${selectedTrades.join(',')}`);
      if (response.status === 200) {
        setSearchResult(response.data);
        if (response.data.no_results) {
          setError('No results found. Please try again.');
        } else {
          setError('');
          setShowSmallSearch(false);
        }
      } else {
        setError('Failed to fetch companies. Please try again.');
      }
    }
  }

  const handleSmallSearchClick = () => {
    setShowSmallSearch(!showSmallSearch);
  }

  const handleSmallCitySelect = (cityId) => {
    setSelectedCity(cityId);
  }

  const handleSmallTradeSelectedChange = (tradeId) => {
    if (selectedTrades.includes(tradeId)) {
      setSelectedTrades(selectedTrades.filter((t) => t !== tradeId));
    } else {
      setSelectedTrades([...selectedTrades, tradeId]);
    }
  }

  return (
      <div className="directoryContainer">
        <div className={`directorySearchContainer ${selectedCity ? 'selected' : 'unselected'}`}>
          <div className="directorySearchTypeContainer cities" onClick={(e) => {
            handleSearchTypeClick('cities', e);
          }}>
            <div className="directorySearchType">
              <div className="directorySearchTypeTitle">
                Location
              </div>
              <div className={`directorySearchTypeBody ${selectedCity ? 'selected' : ''}`}>
                {selectedCity !== '0' ? getCity(selectedCity)?.name : 'Select city'}
              </div>
            </div>
          </div>
          <div className="directorySearchTypeDivider"/>
          <div className="directorySearchTypeContainer trades" onClick={(e) => {
            handleSearchTypeClick('trades', e);
          }}>
            <div className="directorySearchType">
              <div className="directorySearchTypeTitle">
                Categories
              </div>
              <div className={`directorySearchTypeBody ${selectedTrades.length > 0 ? 'selected' : ''}`}>
                {selectedTrades.length > 0 ? getSelectedTradeNames() : 'Select trades'}
              </div>
            </div>
            <div className="directorySearchButtonContainer" onClick={handleSearch}>
              <img src={(selectedCity ? search_button : search_button_compact) || ""} alt="search"
                   className="directorySearchButton"/>
            </div>
          </div>
          <div className={`directorySearchSelectContainer ${clickedSearchType}`} ref={searchSelectContainerRef}>
            <div className="directorySearchCitySelectContainer">
              <div className="directorySearchSelectTitle">Select city</div>
              <div className="directorySearchSelectBody">
                {cities.map((city) => (
                    <div key={city.id} className="directorySearchCityOption" onClick={() => handleCitySelect(city.id)}>
                      <div className="directorySearchCityOptionRadioButton">
                        <img src={(selectedCity === city.id ? city_radio_selected : city_radio_unselected) || ''}
                             alt={city.name}/>
                      </div>
                      <div className="directorySearchCityOptionName">
                        {city.name}
                      </div>
                    </div>
                ))}
              </div>
            </div>
            <div className="directorySearchTradeSelectContainer">
              <div className="directorySearchSelectTitle">Select trades</div>
              <div className="directorySearchSelectBody">
                {trades.map((trade) => (
                    <div key={trade.id}
                         className={`directorySearchTradeOption ${selectedTrades.includes(trade.id) ? 'selected' : 'unselected'}`}
                         onClick={() => handleTradeSelectedChange(trade.id)}>
                      {trade.name}
                    </div>
                ))}
              </div>
            </div>
          </div>
        </div>


        <div className="directorySmallSearchContainer" onClick={handleSmallSearchClick}>
          <div className="directorySmallSearchIconContainer">
            <img src={search_icon || ""} alt="search" className="directorySmallSearchIcon"/>
          </div>
          <div className="directorySmallSearchCityContainer">
            <div className="directorySmallSearchTypeTitle">
              Location
            </div>
            <div className={`directorySmallSearchTypeBody ${selectedCity ? 'selected' : ''}`}>
              {selectedCity !== '0' ? getCity(selectedCity)?.name : 'Select city'}
            </div>
          </div>
        </div>
        {showSmallSearch && (
            <div className="directorySmallSearchDetailsContainer">
              <div className="directorySmallSearchCitySelectContainer">
                <div className="directorySmallSearchSelectTitle">Select city</div>
                <div className="directorySmallSearchSelectBody">
                  {cities.map((city) => (
                      <div key={city.id} className="directorySmallSearchCityOption"
                           onClick={() => handleSmallCitySelect(city.id)}>
                        <div className="directorySmallSearchCityOptionRadioButton">
                          <img src={(selectedCity === city.id ? city_radio_selected : city_radio_unselected) || ''}
                               alt={city.name}/>
                        </div>
                        <div className="directorySmallSearchCityOptionName">
                          {city.name}
                        </div>
                      </div>
                  ))}
                </div>
              </div>
              <div className="directorySmallSearchTradeSelectContainer">
                <div className="directorySmallSearchSelectTitle">Select trades</div>
                <div className="directorySmallSearchSelectBody">
                  {trades.map((trade) => (
                      <div key={trade.id}
                           className={`directorySmallSearchTradeOption ${selectedTrades.includes(trade.id) ? 'selected' : 'unselected'}`}
                           onClick={() => handleSmallTradeSelectedChange(trade.id)}>
                        {trade.name}
                      </div>
                  ))}
                </div>
              </div>
              <div className="directorySmallButtonContainer">
                <div className="directorySmallSearchButtonContainer" onClick={handleSearch}>
                  <img src={(selectedCity ? search_button_small_active : search_button_small) || ""} alt="search"
                       className="directorySmallSearchButton"/>
                </div>
              </div>
            </div>
        )}


        {error && <div className="directoryError">{error}</div>}
        {!showSmallSearch && (
            <div className="directoryListContainer">
              <div className="directoryListCity">{searchResult.city.name}</div>
              {searchResult.trades.map((trade) => (
                  <div>
                    {trade?.companies?.length > 0 && (
                        <div key={trade.id} className="directoryListCityContainer">
                          <div className="directoryListTrade">{trade.name}</div>
                          <div className="directoryListCompanies">
                            {trade.companies.map((company) => (
                                <DirectoryCompany company={company}/>
                            ))}
                          </div>
                        </div>
                    )}
                  </div>
              ))}
            </div>
        )}
        <CTAFooter/>
      </div>
  );
}

export default Directory;
